import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { useContext } from 'react';
import watermarkImage from '../assets/coverzone-watermark.png';
import Header from '../Header';
import Sidebar from '../Sidebar';

const API_URL = process.env.REACT_APP_API_URL;

interface Renewal {
  id: string;
  created_date: string;
  updated_date?: string;
  reference_number: string;
  client_name: string;
  client_id?: string;
  status: string;
  consultant_name: string;
  renewal_method: string;
  description: string;
  policy_id?: string;
  policy_number?: string;
  date_received?: string;
  date_sign_off?: string;
  signed_off_by?: string;
  created_by?: string;
}

interface Consultant {
  id: string;
  name: string;
  surname: string;
  email: string;
}

interface Client {
  id: string;
  name: string;
  surname: string;
  email: string;
  client_type?: string;
  contact_number?: string;
  mobile_number?: string;
  telephone_number?: string;
  organization?: string;
  personal?: boolean;
  commercial?: boolean;
  medical?: boolean;
  client_number?: string;
}

const ManageRenewals = () => {
  const [user, setUser] = useState<{ id: number; email: string; username?: string; organization?: string } | null>(null);
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [renewals, setRenewals] = useState<Renewal[]>([]);
  const [filteredRenewals, setFilteredRenewals] = useState<Renewal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchRenewalTerm, setSearchRenewalTerm] = useState('');
  const [searchConsultantTerm, setSearchConsultantTerm] = useState('');
  const [currentUserFullName, setCurrentUserFullName] = useState<string>('');
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch user data
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
        
        const userData = response.data;
        const fullName = userData.username || userData.email || '';
        if (fullName) {
          setCurrentUserFullName(fullName);
          setSearchConsultantTerm(fullName);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        localStorage.removeItem('token');
        navigate('/login');
      }
    };
    fetchUser();
  }, [navigate]);

  // Fetch renewals
  useEffect(() => {
    const fetchRenewals = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        const response = await axios.get(`${API_URL}/api/manage/renewals`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        console.log('📦 Renewals response:', response.data);
        
        if (response.data.success) {
          const renewalsData = response.data.data || [];
          setRenewals(renewalsData);
          setFilteredRenewals(renewalsData);
        } else {
          throw new Error(response.data.message || 'Failed to fetch renewals');
        }
      } catch (error: any) {
        console.error('❌ Error fetching renewals:', error);
        setError(error.response?.data?.message || 'Failed to load renewals');
        
        // Fallback to mock data
        const mockRenewals: Renewal[] = [
  {
    id: '1',
    created_date: new Date().toISOString().split('T')[0],
    updated_date: new Date().toISOString().split('T')[0],
    reference_number: 'REN-001',
    client_name: 'Sample Client',
    client_id: '1',
    status: 'Open',
    consultant_name: 'Sample Consultant',
    renewal_method: 'Email',
    description: 'Sample renewal description',
    policy_number: 'POL-001',
    date_received: new Date().toISOString().split('T')[0],
    created_by: '1'
  }
];
        setRenewals(mockRenewals);
        setFilteredRenewals(mockRenewals);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRenewals();
  }, []);

  // Search and filter
  useEffect(() => {
    let filtered = renewals;

    if (searchRenewalTerm.trim()) {
  const lowercasedSearch = searchRenewalTerm.toLowerCase().trim();
  filtered = filtered.filter(renewal =>
    renewal.reference_number?.toLowerCase().includes(lowercasedSearch) ||
    renewal.renewal_method?.toLowerCase().includes(lowercasedSearch) ||
    renewal.description?.toLowerCase().includes(lowercasedSearch) ||
    renewal.client_name?.toLowerCase().includes(lowercasedSearch) ||
    renewal.policy_number?.toLowerCase().includes(lowercasedSearch) ||
    'open'.includes(lowercasedSearch)
  );
}

    if (searchConsultantTerm.trim()) {
      const lowercasedSearch = searchConsultantTerm.toLowerCase().trim();
      filtered = filtered.filter(renewal => {
        const renewalConsultantName = renewal.consultant_name?.toLowerCase().trim() || '';
        return renewalConsultantName.includes(lowercasedSearch);
      });
    }

    setFilteredRenewals(filtered);
  }, [searchRenewalTerm, searchConsultantTerm, renewals]);

  const handleLogout = () => {
    authContext?.setUser(null);
    localStorage.removeItem('token');
    navigate('/login');
  };

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error('Failed to load watermark image:', e);
  };

  const handleImageLoad = (type: string) => {
    //console.log(`${type} image loaded successfully`);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount);
  };

  if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex relative">

      {/* Watermark */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0">
        <img
          src={watermarkImage}
          alt="Watermark"
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-100 h-100 opacity-5 z-0 select-none"
          onError={handleImageError}
          onLoad={() => handleImageLoad('Watermark')}
        />
      </div>

      {/* Sidebar */}
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />

      {/* Main Content */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        {/* Header */}
        <Header
          title="Renewals"
          user={user}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
        />

        {/* Page Content */}
        <div className="flex-1 p-6 overflow-auto">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">
              {error}
            </div>
          )}

          <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-[1600px] mx-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Renewals</h2>
            </div>

            {/* Search Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Renewals
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by reference, type, description, client, policy, or status..."
                    value={searchRenewalTerm}
                    onChange={(e) => setSearchRenewalTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Consultant
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by consultant name, surname, or email..."
                    value={searchConsultantTerm}
                    onChange={(e) => setSearchConsultantTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  {searchConsultantTerm && (
                    <button
                      onClick={() => setSearchConsultantTerm('')}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Search Results Info */}
            {(searchRenewalTerm || searchConsultantTerm) && (
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Showing {filteredRenewals.length} of {renewals.length} renewals
                  {searchRenewalTerm && ` matching "${searchRenewalTerm}"`}
                  {searchConsultantTerm && ` for consultant "${searchConsultantTerm}"`}
                </p>
              </div>
            )}

            {/* Renewals List */}
            {isLoading ? (
              <div className="text-center py-8">
                <p className="text-gray-700">Loading renewals...</p>
              </div>
            ) : filteredRenewals.length === 0 ? (
              <div className="text-center py-8">
                {searchRenewalTerm || searchConsultantTerm ? (
                  <p className="text-gray-700">No renewals found matching your search criteria.</p>
                ) : (
                  <p className="text-gray-700">No renewals available.</p>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Updated
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ref No
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Employee / Consultant
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredRenewals.map((renewal) => (
                      <tr key={renewal.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(renewal.created_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(renewal.updated_date || renewal.created_date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => {
                              if (renewal.client_id) {
                                navigate(`/personal/${renewal.client_id}`, { 
                                  state: { 
                                    activeTab: 'renewals',
                                    renewalId: renewal.id 
                                  } 
                                });
                              } else {
                                alert(`Cannot navigate to renewal details. No client associated with this renewal.`);
                              }
                            }}
                            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          >
                            {renewal.reference_number && renewal.reference_number !== 'N/A' 
                              ? renewal.reference_number 
                              : `REN-${String(renewal.id).padStart(3, '0')}`}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {renewal.client_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="max-w-xs truncate" title={renewal.description}>
                            {renewal.description || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {renewal.consultant_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Open
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageRenewals;